import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/landingpage.scss';

const NAV_LINKS = [
	{ id: 'sluzby', label: 'Co nabízíme' },
	{ id: 'galerie', label: 'Náš Gym' },
	{ id: 'kontakt', label: 'Kontakt' },
];

const FEATURES = [
	{
		title: '24/7',
		text: 'Náš gym je otevřen 24 hodin denně, 7 dní v týdnu.',
		icon: (
			<>
				<circle cx="12" cy="12" r="9" />
				<path d="M12 7v5l3 2" />
			</>
		),
	},
	{
		title: 'Správa času',
		text: 'Mějte dokonalý přehled o svých naplánovaných trénincích přímo ve svém telefonu.',
		icon: (
			<>
				<rect x="6" y="2.5" width="12" height="19" rx="2.5" />
				<path d="M10 18.5h4" />
				<path d="M9.5 10.5l1.8 1.8 3.4-3.6" />
			</>
		),
	},
	{
		title: 'Individuální cvičení',
		text: 'Samostatné cvičení nebo lekce s osobním trenérem pro maximální výsledky.',
		icon: (
			<>
				<path d="M6.5 6.5v11M17.5 6.5v11M3.5 9v6M20.5 9v6M6.5 12h11" />
			</>
		),
	},
	{
		title: 'Kruhové tréninky',
		text: 'Maximální intenzita pro spalování tuků a budování funkční síly celého těla v rámci skupinové lekce.',
		icon: (
			<>
				<path d="M20 12a8 8 0 0 1-13.66 5.66" />
				<path d="M4 12a8 8 0 0 1 13.66-5.66" />
				<path d="M17.5 2.5v4h-4" />
				<path d="M6.5 21.5v-4h4" />
			</>
		),
	},
	{
		title: 'Rezervace lekcí',
		text: 'Vyberte si skupinovou lekci a zarezervujte si své místo online během pár vteřin.',
		icon: (
			<>
				<rect x="3" y="4.5" width="18" height="16.5" rx="2.5" />
				<path d="M16 2.5v4M8 2.5v4M3 10h18" />
				<path d="M9 15l2 2 4-4" />
			</>
		),
	},
	{
		title: 'Komunita',
		text: 'Staňte se součástí naší Progress Gym rodiny a dosahujte svých fitness cílů společně s námi.',
		icon: (
			<>
				<circle cx="9" cy="8" r="3.5" />
				<path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
				<path d="M16 4.7a3.5 3.5 0 0 1 0 6.6" />
				<path d="M18 14.4c2.1.8 3.5 2.8 3.5 5.6" />
			</>
		),
	},
];

const PHOTOS = [
	{ src: './images/photos/01.jpg', alt: 'Budova gymu' },
	{ src: './images/photos/02.jpg', alt: 'Budova gymu' },
	{ src: './images/photos/03.jpg', alt: 'Interiér gymu' },
	{ src: './images/photos/04.jpg', alt: 'Interiér gymu' },
	{ src: './images/photos/05.jpg', alt: 'Interiér gymu' },
	{ src: './images/photos/06.jpg', alt: 'Interiér gymu' },
	{ src: './images/photos/07.jpg', alt: 'Interiér gymu' },
	{ src: './images/photos/08.jpg', alt: 'Interiér gymu' },
	{ src: './images/photos/09.jpg', alt: 'Interiér gymu' },
	{ src: './images/photos/10.jpg', alt: 'Interiér gymu' },
	{ src: './images/photos/11.jpg', alt: 'Interiér gymu' },
	{ src: './images/photos/12.jpg', alt: 'Interiér gymu' },
];

const MAP_URL = 'https://mapy.cz/s/dejofonoko';

// Stránka scrolluje v <body> (ne v <html>), proto bereme maximum ze všech zdrojů.
const getScrollTop = () => Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, document.body.scrollTop || 0);

const prefersReducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function Icon({ children, className = 'lp-icon' }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			{children}
		</svg>
	);
}

const ArrowIcon = () => (
	<Icon className="lp-btn__icon">
		<path d="M5 12h14M13 6l6 6-6 6" />
	</Icon>
);

export default function LandingPage() {
	const rootRef = useRef(null);
	const touchStartX = useRef(null);
	const [scrolled, setScrolled] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState(null);

	useEffect(() => {
		const previousBodyOverflow = document.body.style.overflow;
		const previousHtmlOverflow = document.documentElement.style.overflow;

		document.body.style.overflow = 'auto';
		document.documentElement.style.overflow = 'auto';

		return () => {
			document.body.style.overflow = previousBodyOverflow;
			document.documentElement.style.overflow = previousHtmlOverflow;
		};
	}, []);

	// Ztmavení navigace po odscrollování.
	useEffect(() => {
		const onScroll = () => setScrolled(getScrollTop() > 24);
		onScroll();
		document.addEventListener('scroll', onScroll, { capture: true, passive: true });
		return () => document.removeEventListener('scroll', onScroll, { capture: true });
	}, []);

	// Plynulé zobrazování sekcí při scrollu.
	useEffect(() => {
		const items = rootRef.current ? rootRef.current.querySelectorAll('[data-reveal]') : [];

		if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
			items.forEach((el) => el.classList.add('is-visible'));
			return undefined;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add('is-visible');
						observer.unobserve(entry.target);
					}
				});
			},
			{ threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
		);

		items.forEach((el) => observer.observe(el));
		return () => observer.disconnect();
	}, []);

	const closeLightbox = useCallback(() => setLightboxIndex(null), []);
	const showPrev = useCallback(() => setLightboxIndex((i) => (i === null ? i : (i - 1 + PHOTOS.length) % PHOTOS.length)), []);
	const showNext = useCallback(() => setLightboxIndex((i) => (i === null ? i : (i + 1) % PHOTOS.length)), []);

	// Klávesnice pro menu a galerii.
	useEffect(() => {
		const onKey = (e) => {
			if (e.key === 'Escape') {
				setMenuOpen(false);
				closeLightbox();
			}
			if (lightboxIndex !== null) {
				if (e.key === 'ArrowLeft') showPrev();
				if (e.key === 'ArrowRight') showNext();
			}
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [lightboxIndex, closeLightbox, showPrev, showNext]);

	// Při otevřené galerii nebo menu nescrollovat stránku pod ním.
	useEffect(() => {
		if (lightboxIndex === null && !menuOpen) return undefined;
		const previous = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previous;
		};
	}, [lightboxIndex, menuOpen]);

	const scrollToSection = (e, id) => {
		e.preventDefault();
		setMenuOpen(false);
		const target = document.getElementById(id);
		if (target) {
			target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
		}
	};

	const scrollToTop = (e) => {
		e.preventDefault();
		setMenuOpen(false);
		const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
		document.body.scrollTo({ top: 0, behavior });
		document.documentElement.scrollTo({ top: 0, behavior });
	};

	const onTouchStart = (e) => {
		touchStartX.current = e.touches[0].clientX;
	};

	const onTouchEnd = (e) => {
		if (touchStartX.current === null) return;
		const delta = e.changedTouches[0].clientX - touchStartX.current;
		touchStartX.current = null;
		if (Math.abs(delta) < 50) return;
		if (delta > 0) showPrev();
		else showNext();
	};

	const year = new Date().getFullYear();

	return (
		<div className="landingPage" ref={rootRef}>
			{/* ---------- Navigace ---------- */}
			<header className={`lp-nav${scrolled ? ' is-scrolled' : ''}${menuOpen ? ' is-open' : ''}`}>
				<div className="lp-container lp-nav__inner">
					<a href="/" className="lp-nav__logo" onClick={scrollToTop} aria-label="Progress Gym – úvod">
						<img src="./images/logowhite.svg" alt="Progress Gym" />
					</a>

					<nav className="lp-nav__links" aria-label="Hlavní navigace">
						{NAV_LINKS.map((link) => (
							<a key={link.id} href={`#${link.id}`} onClick={(e) => scrollToSection(e, link.id)}>
								{link.label}
							</a>
						))}
					</nav>

					<div className="lp-nav__actions">
						<a href="./login" className="lp-btn lp-btn--ghost lp-btn--sm">Přihlásit</a>
						<a href="./register" className="lp-btn lp-btn--primary lp-btn--sm lp-hide-mobile">Registrovat</a>
						<button
							type="button"
							className="lp-nav__burger"
							aria-label={menuOpen ? 'Zavřít menu' : 'Otevřít menu'}
							aria-expanded={menuOpen}
							aria-controls="lp-mobile-menu"
							onClick={() => setMenuOpen((open) => !open)}
						>
							<span />
							<span />
							<span />
						</button>
					</div>
				</div>

				<div id="lp-mobile-menu" className="lp-nav__mobile" hidden={!menuOpen}>
					{NAV_LINKS.map((link) => (
						<a key={link.id} href={`#${link.id}`} onClick={(e) => scrollToSection(e, link.id)}>
							{link.label}
						</a>
					))}
					<div className="lp-nav__mobile-actions">
						<a href="./login" className="lp-btn lp-btn--ghost">Přihlásit</a>
						<a href="./register" className="lp-btn lp-btn--primary">Registrovat</a>
					</div>
				</div>
			</header>

			<main>
				{/* ---------- Hero ---------- */}
				<section className="lp-hero">
					<div className="lp-hero__media" aria-hidden="true">
						<img src="./images/photos/05.jpg" alt="" />
					</div>
					<div className="lp-hero__glow" aria-hidden="true" />

					<div className="lp-container lp-hero__content">
						<div className="lp-hero__text">
							<span className="lp-eyebrow lp-hero__eyebrow">
								<span className="lp-dot" />
								Progress Gym · Návsí · Otevřeno 24/7
							</span>

							<h1 className="lp-hero__title">
								Posuňte své limity.
								<span className="lp-gradient-text">Začněte dnes.</span>
							</h1>

							<p className="lp-hero__lead">
								Přidejte se k nám do Progress Gymu. Rezervujte si své místo na skupinových lekcích, spravujte své tréninky a sledujte svůj pokrok jednoduše v naší aplikaci.
							</p>

							<div className="lp-hero__actions">
								<a href="./register" className="lp-btn lp-btn--primary lp-btn--lg">
									Stát se členem
									<ArrowIcon />
								</a>
								<a href="./login" className="lp-btn lp-btn--glass lp-btn--lg">Vstoupit do aplikace</a>
							</div>

							<ul className="lp-hero__stats">
								<li>
									<strong>24/7</strong>
									<span>otevřeno</span>
								</li>
								<li>
									<strong>Online</strong>
									<span>rezervace lekcí</span>
								</li>
								<li>
									<strong>1:1</strong>
									<span>osobní trenér</span>
								</li>
							</ul>
						</div>

						<div className="lp-hero__art" aria-hidden="true">
							<img src="./images/landing/fitness-hero.svg" alt="" />
						</div>
					</div>

					<a href="#sluzby" className="lp-hero__scroll" onClick={(e) => scrollToSection(e, 'sluzby')} aria-label="Posunout na obsah">
						<span />
					</a>
				</section>

				{/* ---------- Služby ---------- */}
				<section id="sluzby" className="lp-section">
					<div className="lp-container">
						<div className="lp-section__head" data-reveal>
							<span className="lp-eyebrow">Co nabízíme</span>
							<h2 className="lp-section__title">
								Vše pro váš <span className="lp-gradient-text">progres</span>
							</h2>
						</div>

						<div className="lp-features">
							{FEATURES.map((feature, index) => (
								<article key={feature.title} className="lp-card" data-reveal style={{ '--lp-delay': `${(index % 3) * 80}ms` }}>
									<div className="lp-card__icon">
										<Icon>{feature.icon}</Icon>
									</div>
									<h3 className="lp-card__title">{feature.title}</h3>
									<p className="lp-card__text">{feature.text}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				{/* ---------- Galerie ---------- */}
				<section id="galerie" className="lp-section lp-section--alt">
					<div className="lp-container">
						<div className="lp-section__head" data-reveal>
							<span className="lp-eyebrow">Galerie</span>
							<h2 className="lp-section__title">Náš Gym</h2>
						</div>

						<div className="lp-gallery" data-reveal>
							{PHOTOS.map((photo, index) => (
								<button
									key={photo.src}
									type="button"
									className="lp-gallery__item"
									onClick={() => setLightboxIndex(index)}
									aria-label={`Zobrazit fotografii ${index + 1}: ${photo.alt}`}
								>
									<img src={photo.src} alt={photo.alt} loading="lazy" decoding="async" />
									<span className="lp-gallery__zoom" aria-hidden="true">
										<Icon>
											<circle cx="11" cy="11" r="6.5" />
											<path d="M20 20l-4.2-4.2M11 8.5v5M8.5 11h5" />
										</Icon>
									</span>
								</button>
							))}
						</div>
					</div>
				</section>

				{/* ---------- Kontakt ---------- */}
				<section id="kontakt" className="lp-section">
					<div className="lp-container">
						<div className="lp-contact" data-reveal>
							<div className="lp-contact__body">
								<span className="lp-eyebrow">Kontakt</span>
								<h2 className="lp-section__title lp-contact__title">Kde nás najdete</h2>

								<a className="lp-contact__address" href={MAP_URL} target="_blank" rel="noopener noreferrer">
									<span className="lp-contact__pin">
										<Icon>
											<path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
											<circle cx="12" cy="9.5" r="2.5" />
										</Icon>
									</span>
									<span>
										<small>Adresa</small>
										<strong>Návsí 1160, 739 92 Návsí</strong>
									</span>
								</a>

								<p className="lp-contact__text">Přijďte se podívat a nastartovat svůj progres osobně!</p>

								<div className="lp-contact__actions">
									<a href={MAP_URL} target="_blank" rel="noopener noreferrer" className="lp-btn lp-btn--primary">
										Navigovat na Mapy.cz
										<ArrowIcon />
									</a>
									<a href="./register" className="lp-btn lp-btn--ghost">Stát se členem</a>
								</div>
							</div>

							<div className="lp-contact__media">
								<img src="./images/photos/01.jpg" alt="Budova Progress Gymu v Návsí" loading="lazy" decoding="async" />
								<span className="lp-contact__badge">
									<span className="lp-dot" />
									Otevřeno 24/7
								</span>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* ---------- Patička ---------- */}
			<footer className="lp-footer">
				<div className="lp-container lp-footer__inner">
					<img src="./images/logowhite.svg" alt="Progress Gym" className="lp-footer__logo" />
					<nav className="lp-footer__links" aria-label="Odkazy v patičce">
						<a href="./login">Přihlášení</a>
						<span aria-hidden="true">·</span>
						<a href="./register">Registrace</a>
					</nav>
					<p className="lp-footer__copy">&copy; {year} Progress Gym. Všechna práva vyhrazena.</p>
				</div>
			</footer>

			{/* ---------- Lightbox ---------- */}
			{lightboxIndex !== null && (
				<div className="lp-lightbox" role="dialog" aria-modal="true" aria-label="Galerie fotografií" onClick={closeLightbox} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
					<button type="button" className="lp-lightbox__close" onClick={closeLightbox} aria-label="Zavřít galerii">
						<Icon>
							<path d="M6 6l12 12M18 6L6 18" />
						</Icon>
					</button>

					<button
						type="button"
						className="lp-lightbox__nav lp-lightbox__nav--prev"
						onClick={(e) => {
							e.stopPropagation();
							showPrev();
						}}
						aria-label="Předchozí fotografie"
					>
						<Icon>
							<path d="M15 5l-7 7 7 7" />
						</Icon>
					</button>

					<figure className="lp-lightbox__figure" onClick={(e) => e.stopPropagation()}>
						<img key={lightboxIndex} src={PHOTOS[lightboxIndex].src} alt={PHOTOS[lightboxIndex].alt} />
						<figcaption>
							{lightboxIndex + 1} / {PHOTOS.length}
						</figcaption>
					</figure>

					<button
						type="button"
						className="lp-lightbox__nav lp-lightbox__nav--next"
						onClick={(e) => {
							e.stopPropagation();
							showNext();
						}}
						aria-label="Další fotografie"
					>
						<Icon>
							<path d="M9 5l7 7-7 7" />
						</Icon>
					</button>
				</div>
			)}
		</div>
	);
}
